import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';

import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { startWith } from 'rxjs';
import { GlobalNotificationsBannerService } from '../../../layout/services/global-notifications-banner.service';
import { GlobalNotificationsConfig } from '@ceres/domain';

@Component({
    selector: 'ceres-global-notifications-banner',
    standalone: true,
    imports: [TranslatePipe, TranslateDirective, MatIconModule],
    templateUrl: './global-notifications-banner.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./global-notifications-banner.component.scss']
})
export class GlobalNotificationsBannerComponent {
    private readonly bannerService = inject(GlobalNotificationsBannerService);
    private readonly translateService = inject(TranslateService);
    private readonly languageChange = toSignal(this.translateService.onLangChange.pipe(startWith(null)));

    protected readonly config = this.bannerService.config;
    protected readonly isVisible = this.bannerService.isBannerVisible;
    protected readonly formattedPatchDateTime = computed(() => {
        this.languageChange();
        return this.formatPatchDateTime(this.config());
    });

    private formatPatchDateTime(config: GlobalNotificationsConfig | null): string {
        if (!config) {
            return '';
        }
        const patchDateTime = new Date(config.patchDateTime);
        const endTime = new Date(config.endTime);

        if ([patchDateTime, endTime].some((date) => Number.isNaN(date.getTime()))) {
            return '';
        }

        const dateStr = new Intl.DateTimeFormat('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone: 'Europe/Berlin'
        }).format(patchDateTime);
        const startStr = new Intl.DateTimeFormat('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: 'Europe/Berlin'
        }).format(patchDateTime);
        const endStr = new Intl.DateTimeFormat('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: 'Europe/Berlin'
        }).format(endTime);
        const dateLabel = this.translateService.instant('admin.global-notifications-config.date-label');
        const timeLabel = this.translateService.instant('admin.global-notifications-config.time-label');

        return `${dateLabel}: ${dateStr}, ${timeLabel}: ${startStr} - ${endStr} CET`;
    }
}
